import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { Progress } from "antd";
import i18n from "utils/I18n";

import { enLocaleNumberFormatter, getTranslationField } from "../../utils";
import "./LevelSectionV2.scss"
import { LevelCalculationSource, LevelUpgradeOptionType, MembershipCardBackgroundType } from "utils/CustomEnums";
import MoreUpgradeOptions from "./MoreUpgradeOptions";
import { Color, hexToRgb, Solver } from "utils/ConvertColorToFilter";
import { getLevelIndex } from "utils/LevelUtils";
import { navigateStack } from "utils/WeChatUtil";
import { RightArrow } from "component/base/SvgIcons";

const getSpendMoreValue = ({
  nextLevelData,
  levelUpgradeType,
  pointsToLevelUp,
  spentInThePeriod,
}) => {
  if (levelUpgradeType === LevelUpgradeOptionType.ACCUMULATED_TOTAL) {
    return pointsToLevelUp;
  };
  if (levelUpgradeType === LevelUpgradeOptionType.ACCUMULATED_IN_X_DAYS) {
    return nextLevelData.accumulatedSpendingInThePeriod - spentInThePeriod;
  };
  return nextLevelData.spendingInOnePurchase;
}

const LevelUpgradeInfo = ({
  nextLevelData,
  nextLevelName,
  levelCalculationSource,
  levelUpgradeType,
  upgradeOptions,
  showLevelUpProgress,
  levelUpProgress,
  pointsToLevelUp,
  spentInThePeriod,
  foregroundColor,
  filterCSS,
  openMoreUpgradeOptions,
}) => {
  const spendMoreValue = getSpendMoreValue({
    nextLevelData,
    levelUpgradeType,
    pointsToLevelUp,
    spentInThePeriod,
  });

  return (
    <div>
      <div className="subheading">
        {i18n.t(
          levelUpgradeType === LevelUpgradeOptionType.ACCUMULATED_TOTAL &&
            levelCalculationSource === LevelCalculationSource.PROGRESSION_POINTS
            ? 'earn'
            : 'spend'
        )}
        {i18n.t(
          levelUpgradeType === LevelUpgradeOptionType.ACCUMULATED_TOTAL &&
            levelCalculationSource === LevelCalculationSource.PROGRESSION_POINTS
            ? 'PP'
            : 'HKD',
          {
            TPE: enLocaleNumberFormatter(spendMoreValue),
          })}
        {i18n.t(levelUpgradeType === LevelUpgradeOptionType.SPENDING_IN_ONE_PURCHASE ? 'orAbove' : 'more')}
      </div>
      <div className="tag-menu-1">
        {
          levelUpgradeType === LevelUpgradeOptionType.ACCUMULATED_IN_X_DAYS
            ? i18n.t("HKDInXDays", {
              amount: enLocaleNumberFormatter(nextLevelData.accumulatedSpendingInThePeriod),
              day: nextLevelData.accumulatedSpendingInXDay
            })
            : null
        }
        {
          levelUpgradeType === LevelUpgradeOptionType.SPENDING_IN_ONE_PURCHASE
            ? i18n.t("inASinglePurchase")
            : null
        }
        {i18n.t('toUpgrade', { nextLevelName })}
      </div>
      {
        showLevelUpProgress && (
          <div>
            <Progress
              className="upgrade-progress"
              percent={levelUpProgress * 100}
              showInfo={false}
              strokeWidth={8}
              {
              ...(
                foregroundColor
                  ? { strokeColor: foregroundColor }
                  : null
              )
              }
            />
          </div>
        )
      }
      {
        upgradeOptions.length > 0 && (
          <div className="more-upgrade-options-container" >
            <div className="tag-menu-1">
              {i18n.t("moreUpgradeOptions")}
            </div>
            <div className="more-upgrade-options-image" onClick={openMoreUpgradeOptions}>
              <img
                alt="icon"
                src="/profile/retainLevel/info.png"
                style={filterCSS ? { filter: filterCSS } : null}
              />
            </div>
          </div>
        )
      }
    </div>
  )
}

// level rules / level benefits at the bottom of the card
const LevelCardLevelRules = () => {
  const history = useHistory()
  return <div className="card-bottom-level-rules-section tag-menu-1">
    <div
      onClick={() => {
        navigateStack({ pathname: "/level_rule", history })
      }}
    >
      <div>
        {i18n.t("levelRules")}
      </div>
      <RightArrow />
    </div>
    <div
      onClick={() => {
        navigateStack({ pathname: "/level_benefit", history })
      }}
    >
      <div>
        {i18n.t("level_benefit")}
      </div>
      <RightArrow />
    </div>
  </div>
}

const LevelSection = ({
  language,
  userName,
  level,
  levelConfig,
  currentLevelData,
  nextLevelData,
  levelCalculationSource,
  levelUpgradeType,
  upgradeOptions,
  showLevelUpProgress,
  levelUpProgress,
  pointsToLevelUp,
  spentInThePeriod,
}) => {
  const history = useHistory();
  const [showModal, setShowModal] = useState(false);

  const openMoreUpgradeOptions = () => {
    setShowModal(true);
  };

  const closeMoreUpgradeOptions = () => {
    setShowModal(false);
  };

  const goToMyCrd = () => {
    navigateStack({ pathname: "/user/myCard", history })
  };

  const levelIndex = useMemo(() => {
    return getLevelIndex(level, levelConfig)
  }, [level, levelConfig]);

  const currentLevelName = getTranslationField(currentLevelData, "levelName", language);
  const nextLevelName = getTranslationField(nextLevelData, "levelName", language);
  const foregroundColor = currentLevelData.cardForegroundColor;
  const useCurrentLevelSingleColor = currentLevelData.cardBackgroundType === MembershipCardBackgroundType.SINGLE_COLOR;
  const useCurrentLevelImage = currentLevelData.cardBackgroundType === MembershipCardBackgroundType.IMAGE;

  const filterCSS = useMemo(() => {
    const rgb = hexToRgb(foregroundColor);
    if (!rgb) {
      return null;
    };

    const color = new Color(rgb.r, rgb.g, rgb.b);
    const solver = new Solver(color);
    const result = solver.solve();
    const filterCSS = result.filter;
    console.log("@@136", filterCSS)
    return filterCSS;
  }, [foregroundColor]);

  return (
    <div className="profile-level-section">
      {
        useCurrentLevelImage
          ? <img
            alt=""
            src={currentLevelData.membershipCardbackgroundImage}
            className="level-section-background-image level-section-content-config"
          />
          : null
      }
      <div
        className={`level-section-content level-section-content-config level-${levelIndex + 1}-color headline`}
        style={{
          ...(
            useCurrentLevelSingleColor
              ? { background: currentLevelData.cardBackgroundColor }
              : null
          ),
          ...(
            useCurrentLevelImage
              ? { background: "none" }
              : null
          ),
          ...(
            foregroundColor
              ? { color: foregroundColor }
              : null
          ),
        }}
      >
        <div className="d-flex level-section-header">
          <div>
            <div>{`${userName}`}</div>
            <div className="level-text body-3">{currentLevelName}</div>
          </div>
          <div
            className="qrcode-icon"
            onClick={goToMyCrd}
          >
            <img alt="icon" src="/profile/level/qr-code.png" />
          </div>
        </div>

        {
          nextLevelData.hideLevelDetails
            ? <div>
              <div className="subheading">
                {i18n.t("hideLevelTitle")}
              </div>
              <div className="tag-menu-1">
                {i18n.t("hideLevelContent")}
              </div>
            </div>
            : null
        }

        {!nextLevelData.hideLevelDetails && !!levelUpgradeType && (
          <LevelUpgradeInfo
            nextLevelData={nextLevelData}
            nextLevelName={nextLevelName}
            levelCalculationSource={levelCalculationSource}
            levelUpgradeType={levelUpgradeType}
            upgradeOptions={upgradeOptions}
            showLevelUpProgress={showLevelUpProgress}
            levelUpProgress={levelUpProgress}
            pointsToLevelUp={pointsToLevelUp}
            spentInThePeriod={spentInThePeriod}
            foregroundColor={foregroundColor}
            filterCSS={filterCSS}
            openMoreUpgradeOptions={openMoreUpgradeOptions}
          />
        )
        }
      </div>
      <MoreUpgradeOptions
        showModal={showModal}
        close={closeMoreUpgradeOptions}
      />
      <LevelCardLevelRules />
    </div >
  )
}

const mapStateToProps = (state) => ({
  language: state.users.language,
  userName: state.users.displayName,
  level: state.users.level,
  levelConfig: state.levels.levelConfig,
  currentLevelData: state.users.currentLevelData,
  nextLevelData: state.users.nextLevelData,
  levelCalculationSource: state.users.levelCalculationSource,
  levelUpgradeType: state.users.levelUpgradeType,
  upgradeOptions: state.users.upgradeOptions,
  showLevelUpProgress: state.users.showLevelUpProgress,
  levelUpProgress: state.users.levelUpProgress,
  pointsToLevelUp: state.users.pointsToLevelUp,
  spentInThePeriod: state.users.spentInThePeriod,
})

export default connect(mapStateToProps)(LevelSection);
